import React, { useEffect, useState } from 'react';

const MONKEY_LOGO = 'https://my-monkey.fr/assets/10638168_hidden.webp';
const MONKEY_HERO = 'https://my-monkey.fr/assets/10638155_hidden.webp';

// Le minimum qu'applique Better Auth côté serveur : le refuser ici évite un aller-retour
// pour se faire répondre la même chose.
const MIN_PASSWORD = 8;

/**
 * Authentication modal mirroring the shared MY MONKEY design used across
 * the other my-monkey apps (kebab-shit-map, movies, geography). Supports
 * email/password sign-in, sign-up with username, Google OAuth, and a
 * welcome state after sign-up. Signing up from an anonymous session converts
 * it into a real account server-side, carrying the codex over.
 */
export default function AuthModal({
  open,
  onClose,
  isAnonymous,
  onSignIn,
  onSignUp,
  onSignInWithGoogle,
  defaultMode = 'signin',
}) {
  const [mode, setMode] = useState(defaultMode);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Reset the form each time the modal opens
  useEffect(() => {
    if (open) {
      setMode(defaultMode);
      setUsername('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setError(null);
      setSuccess(false);
      setLoading(false);
    }
  }, [open, defaultMode]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose]);

  if (!open) return null;

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const res = await onSignIn(email.trim(), password);
    if (res.error) {
      setError(res.error);
      setLoading(false);
    } else {
      setLoading(false);
      setTimeout(onClose, 400);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    if (password.length < MIN_PASSWORD) {
      setError(`Le mot de passe doit contenir au moins ${MIN_PASSWORD} caractères.`);
      return;
    }

    setLoading(true);
    const res = await onSignUp(email.trim(), password, { name: username });
    if (res.error) {
      setError(res.error);
      setLoading(false);
      return;
    }
    // Le compte est ouvert et la session déjà active : on félicite, on ne renvoie pas
    // le joueur vers sa boîte mail.
    setSuccess(true);
    setLoading(false);
  };

  const handleGoogle = async () => {
    setError(null);
    const res = await onSignInWithGoogle();
    if (res.error) setError(res.error);
    // On success, browser is navigated away — nothing more to do here.
  };

  // ---------- Render ----------

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6"
      style={{ background: 'rgba(27, 29, 14, 0.55)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-[420px]"
        onClick={(e) => e.stopPropagation()}
      >
        {success ? (
          <SuccessCard onContinue={onClose} />
        ) : (
          <div
            className="bg-[#FFFBEB] border-[4px] border-[#1E1E1E] overflow-hidden"
            style={{ boxShadow: '8px 8px 0 #1E1E1E' }}
          >
            {/* Header band */}
            <div className="bg-[#FF6B35] border-b-[4px] border-[#1E1E1E] px-6 py-4 relative overflow-hidden">
              <button
                onClick={onClose}
                className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center border-[2px] border-[#1E1E1E] bg-[#FFFBEB] text-[#1E1E1E] font-bold hover:bg-[#FFF7D6] z-10"
                aria-label="Fermer"
                style={{ boxShadow: '2px 2px 0 #1E1E1E' }}
              >
                ✕
              </button>
              <div className="absolute -right-4 -top-2 opacity-20 pointer-events-none">
                <img src={MONKEY_HERO} alt="" className="w-28 h-28 object-contain rotate-12" />
              </div>
              <div className="flex items-center gap-3">
                <img
                  src={MONKEY_LOGO}
                  alt="My-Monkey"
                  className="w-10 h-10 border-[3px] border-[#1E1E1E] rounded-full bg-[#FFFBEB]"
                />
                <div>
                  <h1
                    className="text-[#FFFBEB] text-2xl leading-none"
                    style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.02em' }}
                  >
                    MY MONKEY
                  </h1>
                  <p
                    className="text-[#FFFBEB]/80 text-xs mt-0.5"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    Ton compte unique pour toutes nos apps
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <h2
                className="text-[#1E1E1E] text-lg font-bold text-center mb-1"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {mode === 'signin'
                  ? 'Re-bienvenue, combattant !'
                  : "Rejoins l'armée des allocataires !"}
              </h2>
              <p
                className="text-[#4A4A4A] text-xs text-center mb-5"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {mode === 'signin'
                  ? 'Connexion à Alloc Warrior via ton compte My-Monkey'
                  : 'Inscription à Alloc Warrior via ton compte My-Monkey'}
              </p>

              {/* Anonymous upgrade notice */}
              {mode === 'signup' && isAnonymous && (
                <div
                  className="bg-[#2ECC71]/10 border-[2px] border-[#2ECC71]/40 p-2 mb-4"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  <p className="text-[11px] text-[#1E1E1E] leading-snug text-center">
                    ✨ Ta progression actuelle sera <strong>préservée</strong> et liée à ton nouveau compte.
                  </p>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="bg-[#FF5E78]/10 border-[2px] border-[#FF5E78]/30 p-2 mb-4">
                  <p
                    className="text-sm text-[#FF5E78] font-bold text-center"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    {error}
                  </p>
                </div>
              )}

              {/* Form */}
              {mode === 'signin' ? (
                <form onSubmit={handleSignIn} className="space-y-3">
                  <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="votre@email.com" autoFocus />
                  <Field label="Mot de passe" type="password" value={password} onChange={setPassword} placeholder="Votre mot de passe" />
                  <SubmitButton loading={loading}>
                    {loading ? 'Connexion…' : 'Se connecter'}
                  </SubmitButton>
                </form>
              ) : (
                <form onSubmit={handleSignUp} className="space-y-3">
                  <Field label="Nom d'utilisateur" type="text" value={username} onChange={setUsername} placeholder="Ton pseudo" autoFocus required />
                  <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="ton@email.com" />
                  <Field label="Mot de passe" type="password" value={password} onChange={setPassword} placeholder={`Au moins ${MIN_PASSWORD} caractères`} minLength={MIN_PASSWORD} />
                  <Field label="Confirmer le mot de passe" type="password" value={confirmPassword} onChange={setConfirmPassword} placeholder="Retape ton mot de passe" minLength={MIN_PASSWORD} />
                  <SubmitButton loading={loading}>
                    {loading ? 'Inscription…' : 'Rejoindre My-Monkey'}
                  </SubmitButton>
                </form>
              )}

              {/* Divider */}
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-[3px] bg-[#1E1E1E]/10" />
                <span
                  className="text-xs font-bold text-[#4A4A4A]"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  ou
                </span>
                <div className="flex-1 h-[3px] bg-[#1E1E1E]/10" />
              </div>

              {/* Google */}
              <button
                onClick={handleGoogle}
                type="button"
                className="w-full flex items-center justify-center gap-3 py-3 bg-white border-[3px] border-[#1E1E1E] text-[#1E1E1E] font-bold text-sm hover:translate-x-[1px] hover:translate-y-[1px] transition-transform"
                style={{ fontFamily: "'Space Grotesk', sans-serif", boxShadow: '4px 4px 0 #1E1E1E' }}
              >
                <GoogleIcon />
                {mode === 'signin' ? 'Continuer avec Google' : "S'inscrire avec Google"}
              </button>

              {/* Mode switch */}
              <p
                className="text-center text-sm text-[#4A4A4A] mt-4"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {mode === 'signin' ? (
                  <button
                    type="button"
                    onClick={() => {
                      setMode('signup');
                      setError(null);
                    }}
                    className="font-bold hover:text-[#FF6B35] transition-colors underline decoration-2 underline-offset-2"
                  >
                    Pas encore de compte My-Monkey ?
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setMode('signin');
                      setError(null);
                    }}
                    className="font-bold hover:text-[#FF6B35] transition-colors underline decoration-2 underline-offset-2"
                  >
                    Déjà un compte My-Monkey ?
                  </button>
                )}
              </p>
              <p
                className="text-center text-xs text-[#4A4A4A] mt-2"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                <button
                  type="button"
                  onClick={onClose}
                  className="hover:text-[#FF6B35] transition-colors"
                >
                  Continuer sans compte
                </button>
              </p>
            </div>

            {/* Footer */}
            <div className="bg-[#FFF7D6] border-t-[3px] border-[#1E1E1E]/10 px-6 py-3 flex items-center justify-center gap-2">
              <span
                className="text-[10px] text-[#4A4A4A]"
                style={{ fontFamily: "'Space Mono', monospace" }}
              >
                propulse par
              </span>
              <a
                href="https://my-monkey.fr"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 hover:opacity-80 transition-opacity"
              >
                <img src={MONKEY_LOGO} alt="" className="w-4 h-4" />
                <span
                  className="text-xs font-bold text-[#1E1E1E]"
                  style={{ fontFamily: "'Bangers', cursive" }}
                >
                  MY MONKEY
                </span>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Field({ label, type, value, onChange, placeholder, autoFocus, minLength, required = true }) {
  return (
    <div>
      <label
        className="block text-xs font-bold text-[#1E1E1E] mb-1"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        autoFocus={autoFocus}
        minLength={minLength}
        className="w-full px-3 py-2.5 bg-white border-[3px] border-[#1E1E1E] text-[#1E1E1E] text-sm focus:border-[#FF6B35] focus:outline-none"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      />
    </div>
  );
}

function SubmitButton({ loading, children }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="w-full py-3 bg-[#FF6B35] text-[#FFFBEB] font-bold text-base border-[3px] border-[#1E1E1E] disabled:opacity-50 hover:translate-x-[1px] hover:translate-y-[1px] transition-transform"
      style={{
        fontFamily: "'Bangers', cursive",
        letterSpacing: '0.05em',
        boxShadow: '4px 4px 0 #1E1E1E',
      }}
    >
      {children}
    </button>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

function SuccessCard({ onContinue }) {
  return (
    <div
      className="bg-[#FFFBEB] border-[4px] border-[#1E1E1E] p-8 text-center"
      style={{ boxShadow: '8px 8px 0 #1E1E1E' }}
    >
      <div className="w-16 h-16 mx-auto mb-4 border-[3px] border-[#1E1E1E] rounded-full bg-[#2ECC71]/10 flex items-center justify-center">
        <svg className="w-8 h-8 text-[#2ECC71]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="text-2xl text-[#1E1E1E] mb-2" style={{ fontFamily: "'Bangers', cursive" }}>
        Bienvenue dans l'armée !
      </h2>
      <p className="text-[#4A4A4A] text-sm mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
        Ton compte My-Monkey est ouvert, et ta progression est sauvegardée avec.
      </p>
      <button
        type="button"
        onClick={onContinue}
        className="inline-block px-6 py-2.5 bg-[#FF6B35] text-[#FFFBEB] font-bold border-[3px] border-[#1E1E1E] hover:translate-x-[1px] hover:translate-y-[1px] transition-transform"
        style={{ fontFamily: "'Bangers', cursive", boxShadow: '4px 4px 0 #1E1E1E' }}
      >
        Reprendre le combat
      </button>
    </div>
  );
}
